// Fills the debug panel (Logger::show_profiler_panel, outside the page area)
// with every request this page view makes: the page's own, each client
// navigation's, and every fetch or XMLHttpRequest whose JSON answer carries
// the request's SQL (queries_ran, Site_Main_Controller::json), newest first.
// Pages put their request in the page area (Logger::show_profiler_request);
// this moves it into the panel on load and after each navigation. A full load
// starts the list over, so a link that empties it isn't client-navigated.

const HEAVY_QUERY_COUNT = 25;
const SLOW_QUERY_SECONDS = 0.5;

// "SELECT ... (0.00012s)" lines, as Site_Main_Controller::json writes them.
export function parseQueriesRan(lines) {
	return lines.map((line) => {
		const match = /^([\s\S]*) \(([\d.E+-]+)s\)$/.exec(line);
		return match ? { sql: match[1], seconds: Number(match[2]) } : { sql: line, seconds: 0 };
	});
}

function element(rootDocument, tag, className, text) {
	const node = rootDocument.createElement(tag);
	if(className) {
		node.className = className;
	}
	if(text !== undefined) {
		node.textContent = text;
	}
	return node;
}

function displayUrl(url, location) {
	try {
		const parsed = new URL(url, location.href);
		return parsed.origin === location.origin ? parsed.pathname + parsed.search : parsed.href;
	} catch(error) {
		return String(url);
	}
}

// The same markup as plugins/models/logger/log/profiler_request.php, without
// the callers, which JSON answers don't carry.
function apiEntry(rootDocument, { method, url, data, milliseconds }) {
	const queries = parseQueriesRan(data.queries_ran);
	const timesUsed = {};
	queries.forEach(({ sql }) => {
		timesUsed[sql] = (timesUsed[sql] || 0) + 1;
	});

	const entry = element(rootDocument, 'details', 'debug-request');
	entry.dataset.kind = 'api';
	entry.dataset.queryCount = String(queries.length);

	const summary = element(rootDocument, 'summary', 'debug-request__summary');
	summary.append(
		element(rootDocument, 'span', 'debug-request__kind', 'API'),
		element(rootDocument, 'span', 'debug-request__method', method),
		element(rootDocument, 'span', 'debug-request__url', url),
		element(rootDocument, 'span', 'debug-request__count', `${queries.length} queries`),
		element(rootDocument, 'span', 'debug-request__time', `${data.execution_time || '?'} · ${Math.round(milliseconds)}ms round trip`),
	);
	entry.append(summary);

	const table = element(rootDocument, 'table', 'queries_table queries table');
	const headings = element(rootDocument, 'tr');
	[['number_heading', '#'], ['query_heading', 'Query'], ['time_heading', 'Time'], ['times_used_heading', 'Times Used']]
		.forEach(([className, label]) => headings.append(element(rootDocument, 'th', className, label)));
	const head = element(rootDocument, 'thead');
	head.append(headings);
	const body = element(rootDocument, 'tbody');
	queries.forEach(({ sql, seconds }, index) => {
		const row = element(rootDocument, 'tr', seconds > SLOW_QUERY_SECONDS && index > 0 ? 'query error' : 'query');
		const number = element(rootDocument, 'td', 'query_number', String(index + 1));
		number.dataset.number = String(index + 1);
		const ran = element(rootDocument, 'td', 'query_ran');
		ran.append(element(rootDocument, 'span', 'query_text', sql));
		const elapsed = element(rootDocument, 'td', 'query_elapsed', `${seconds}s`);
		elapsed.dataset.seconds = String(seconds);
		row.append(number, ran, elapsed, element(rootDocument, 'td', 'times_used', String(timesUsed[sql])));
		body.append(row);
	});
	table.append(head, body);
	entry.append(table);
	return entry;
}

const cellNumber = (row, selector, key) => Number(row.querySelector(selector)?.dataset[key] || 0);
const callOrder = (row) => cellNumber(row, '.query_number', 'number');
const seconds = (row) => cellNumber(row, '.query_elapsed', 'seconds');
const queryText = (row) => row.querySelector('.query_text')?.textContent || '';
const timesUsed = (row) => Number(row.querySelector('.times_used')?.textContent.trim() || 0);

// Each heading's order; ties fall back to call order.
const QUERY_ORDERS = {
	number_heading: () => 0,
	time_heading: (first, second) => seconds(second) - seconds(first),
	query_heading: (first, second) => queryText(first).localeCompare(queryText(second)),
	times_used_heading: (first, second) => timesUsed(second) - timesUsed(first) || queryText(first).localeCompare(queryText(second)),
};

function sortQueries(entry, headingClass) {
	const body = entry.querySelector('.queries_table tbody');
	if(!body) {
		return;
	}
	const order = QUERY_ORDERS[headingClass];
	[...body.querySelectorAll('.query')]
		.sort((first, second) => order(first, second) || callOrder(first) - callOrder(second))
		.forEach((row) => body.append(row));
	entry.querySelectorAll('.queries_table th').forEach((heading) => {
		heading.classList.toggle('sorted', heading.classList.contains(headingClass));
	});
}

function hasQueries(data) {
	return !!data && typeof data === 'object' && Array.isArray(data.queries_ran);
}

function isJson(contentType) {
	return (contentType || '').indexOf('json') !== -1;
}

// Returns the unbind, which puts fetch and XMLHttpRequest back.
export function bindDebugRequests(win, rootDocument) {
	const panel = rootDocument.getElementById('profiler');
	const list = panel && panel.querySelector('.debug-panel__requests');
	if(!list) {
		return () => {};
	}
	const unbinds = [() => win.removeEventListener('smashladder:navigated', adoptPageRequests)];
	let pagesSeen = 0;

	const markHeavy = (entry) => {
		entry.classList.toggle('debug-request--heavy', Number(entry.dataset.queryCount) > HEAVY_QUERY_COUNT);
	};

	const showInPill = (entry) => {
		panel.querySelector('.debug-panel__pill-count').textContent = `${entry.dataset.queryCount} queries`;
		panel.querySelector('.debug-panel__pill-time').textContent = `${entry.dataset.seconds}s`;
	};

	const adoptPageRequests = () => {
		rootDocument.querySelectorAll('.debug-panel-incoming').forEach((incoming) => {
			incoming.querySelectorAll('.debug-request').forEach((entry) => {
				entry.querySelector('.debug-request__kind').textContent = pagesSeen === 0 ? 'Full load' : 'Navigation';
				pagesSeen++;
				list.querySelectorAll('.debug-request[data-kind="page"]').forEach((older) => {
					older.open = false;
				});
				entry.open = true;
				markHeavy(entry);
				sortQueries(entry, 'time_heading');
				list.prepend(entry);
				showInPill(entry);
			});
			incoming.remove();
		});
	};

	const addApiRequest = (request) => {
		if(!hasQueries(request.data)) {
			return;
		}
		const entry = apiEntry(rootDocument, request);
		markHeavy(entry);
		sortQueries(entry, 'time_heading');
		list.prepend(entry);
	};

	adoptPageRequests();
	win.addEventListener('smashladder:navigated', adoptPageRequests);

	const sortByHeading = (event) => {
		const heading = event.target.closest('.queries_table th');
		const headingClass = heading && Object.keys(QUERY_ORDERS).find((name) => heading.classList.contains(name));
		if(headingClass) {
			sortQueries(heading.closest('.debug-request'), headingClass);
		}
	};
	list.addEventListener('click', sortByHeading);
	unbinds.push(() => list.removeEventListener('click', sortByHeading));

	const clear = panel.querySelector('.debug-panel__clear');
	if(clear) {
		clear.addEventListener('click', () => {
			const currentPage = list.querySelector('.debug-request[data-kind="page"]');
			[...list.children].forEach((entry) => {
				if(entry !== currentPage) {
					entry.remove();
				}
			});
		});
	}

	const now = () => (win.performance ? win.performance.now() : Date.now());

	if(typeof win.fetch === 'function') {
		const originalFetch = win.fetch;
		unbinds.push(() => {
			win.fetch = originalFetch;
		});
		win.fetch = function(input, init) {
			const started = now();
			const url = typeof input === 'string' || input instanceof win.URL ? String(input) : input.url;
			const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
			const answer = originalFetch.apply(this, arguments);
			answer.then((response) => {
				if(!response || !response.headers || !isJson(response.headers.get('content-type'))) {
					return;
				}
				const milliseconds = now() - started;
				response.clone().json().then((data) => addApiRequest({
					method,
					url: displayUrl(url, win.location),
					data,
					milliseconds,
				}), () => {});
			}, () => {});
			return answer;
		};
	}

	const xhrPrototype = win.XMLHttpRequest && win.XMLHttpRequest.prototype;
	if(xhrPrototype) {
		const originalOpen = xhrPrototype.open;
		const originalSend = xhrPrototype.send;
		unbinds.push(() => {
			xhrPrototype.open = originalOpen;
			xhrPrototype.send = originalSend;
		});
		xhrPrototype.open = function(method, url) {
			this.debugRequest = { method: String(method).toUpperCase(), url: String(url) };
			return originalOpen.apply(this, arguments);
		};
		xhrPrototype.send = function() {
			const request = this.debugRequest;
			if(request) {
				const started = now();
				this.addEventListener('loadend', () => {
					let data = null;
					try {
						if(this.responseType === 'json') {
							data = this.response;
						} else if((this.responseType === '' || this.responseType === 'text') && isJson(this.getResponseHeader('content-type'))) {
							data = JSON.parse(this.responseText);
						}
					} catch(error) {
						return;
					}
					addApiRequest({
						method: request.method,
						url: displayUrl(request.url, win.location),
						data,
						milliseconds: now() - started,
					});
				});
			}
			return originalSend.apply(this, arguments);
		};
	}

	return () => unbinds.forEach((unbind) => unbind());
}
